import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Search, X, Users, Loader } from 'lucide-react';
import { UserDto, usersApi } from '../../services/usersService';

interface Props {
  selectedSalesReps: UserDto[];
  onChange: (next: UserDto[]) => void;
  disabled?: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
`;

const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 0.875rem;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #0073ea;
    box-shadow: 0 0 0 2px rgba(0, 115, 234, 0.15);
  }

  &:disabled {
    background-color: #f1f3f5;
    cursor: not-allowed;
  }
`;

const SearchIconWrap = styled.span`
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  color: #6c757d;
  pointer-events: none;
`;

const AddAllButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.85rem;
  background: #fff;
  color: #0073ea;
  border: 1px solid #0073ea;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #e6f0ff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Dropdown = styled.ul`
  position: fixed;
  z-index: 2000;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  list-style: none;
  margin: 0;
  padding: 0.25rem 0;
  max-height: 280px;
  overflow-y: auto;
`;

const DropdownItem = styled.li`
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    background: #f1f5fb;
  }

  &:first-child {
    background: #f1f5fb;
  }

  &:first-child:hover {
    background: #e3edfa;
  }
`;

const DropdownEmpty = styled.li`
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  color: #6c757d;
  font-style: italic;
`;

const TagsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.5rem 0.3rem 0.75rem;
  background: #e6f0ff;
  border: 1px solid #c2dbff;
  border-radius: 999px;
  font-size: 0.85rem;
  color: #0959b8;
`;

const RemoveTagButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: transparent;
  color: #0959b8;
  border: none;
  cursor: pointer;
  padding: 0;

  &:hover:not(:disabled) {
    background: rgba(9, 89, 184, 0.12);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Spinner = styled(Loader)`
  animation: spin 0.9s linear infinite;
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  font-size: 0.85rem;
  color: #6c757d;
  padding: 0.25rem 0;
`;

const SalesRepPicker: React.FC<Props> = ({ selectedSalesReps, onChange, disabled = false }) => {
  const [query, setQuery] = useState('');
  const [allReps, setAllReps] = useState<UserDto[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);

  // טעינת כל נציגי המכירות פעם אחת (אין endpoint חיפוש — מסננים בצד לקוח)
  const ensureRepsLoaded = () => {
    if (loaded || loading) return;
    setLoading(true);
    usersApi
      .getSalesReps()
      .then(list => {
        setAllReps(list);
        setLoaded(true);
      })
      .catch(() => setAllReps([]))
      .finally(() => setLoading(false));
  };

  // טעינה ראשונית של רשימת הנציגים
  useEffect(() => {
    setLoading(true);
    usersApi
      .getSalesReps()
      .then(list => {
        setAllReps(list);
        setLoaded(true);
      })
      .catch(() => setAllReps([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideWrapper = wrapperRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideWrapper && !insideDropdown) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (!showDropdown) return;

    const updatePosition = () => {
      if (!inputRef.current) return;
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showDropdown]);

  const selectedIds = new Set(selectedSalesReps.map(s => s.id));
  const normalizedQuery = query.trim().toLowerCase();
  const visibleResults = allReps
    .filter(s => !selectedIds.has(s.id))
    .filter(s => !normalizedQuery || s.fullName.toLowerCase().includes(normalizedQuery));

  const handlePick = (rep: UserDto) => {
    if (selectedIds.has(rep.id)) return;
    onChange([...selectedSalesReps, rep]);
    setQuery('');
    setShowDropdown(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (visibleResults.length > 0) {
        handlePick(visibleResults[0]);
      }
    }
  };

  const handleRemove = (id: number) => {
    onChange(selectedSalesReps.filter(s => s.id !== id));
  };

  const handleAddAll = () => {
    if (disabled) return;
    const existingIds = new Set(selectedSalesReps.map(s => s.id));
    const merged = [
      ...selectedSalesReps,
      ...allReps.filter(s => !existingIds.has(s.id)),
    ];
    onChange(merged);
  };

  return (
    <Container>
      <SearchRow>
        <SearchWrapper ref={wrapperRef}>
          <SearchInput
            ref={inputRef}
            type="text"
            placeholder="חפש נציג מכירות לפי שם..."
            value={query}
            onChange={e => {
              setQuery(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => {
              ensureRepsLoaded();
              setShowDropdown(true);
            }}
            onKeyDown={handleKeyDown}
            disabled={disabled}
          />
          <SearchIconWrap>
            {loading ? <Spinner size={14} /> : <Search size={14} strokeWidth={2} />}
          </SearchIconWrap>
          {showDropdown && (
            <Dropdown
              ref={dropdownRef}
              style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
              onWheel={e => e.stopPropagation()}
            >
              {visibleResults.length === 0 && !loading ? (
                <DropdownEmpty>לא נמצאו נציגי מכירות תואמים</DropdownEmpty>
              ) : (
                visibleResults.map(s => (
                  <DropdownItem
                    key={s.id}
                    onMouseDown={e => {
                      e.preventDefault();
                      handlePick(s);
                    }}
                  >
                    {s.fullName}
                  </DropdownItem>
                ))
              )}
            </Dropdown>
          )}
        </SearchWrapper>
        <AddAllButton
          type="button"
          onClick={() => {
            ensureRepsLoaded();
            handleAddAll();
          }}
          disabled={disabled}
        >
          <Users size={14} strokeWidth={2} />
          הוסף כולם
        </AddAllButton>
      </SearchRow>

      {selectedSalesReps.length === 0 ? (
        <EmptyState>לא נבחרו נציגי מכירות</EmptyState>
      ) : (
        <TagsList>
          {selectedSalesReps.map(s => (
            <Tag key={s.id}>
              {s.fullName}
              <RemoveTagButton
                type="button"
                onClick={() => handleRemove(s.id)}
                disabled={disabled}
                title="הסר נציג"
              >
                <X size={12} strokeWidth={2.5} />
              </RemoveTagButton>
            </Tag>
          ))}
        </TagsList>
      )}
    </Container>
  );
};

export default SalesRepPicker;
